import React, { useEffect, useState } from 'react';
import { cn } from '../../lib/utils';
import SafeIcon from '../common/SafeIcon';
import { FiActivity, FiExternalLink, FiCreditCard, FiZap } from 'react-icons/fi';
import { macros } from '../../data/mockData';

export default function ContextPanel({ ticket }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [isSlaBreaching, setIsSlaBreaching] = useState(false);

  useEffect(() => {
    if (!ticket) return;

    const updateSLA = () => {
      const now = new Date();
      const deadline = new Date(ticket.slaDeadline);
      const diff = deadline - now;

      if (diff <= 0) {
        setTimeLeft('BREACHED');
        setIsSlaBreaching(true);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeLeft(`${hours}h ${minutes}m`);
      setIsSlaBreaching(hours < 1);
    };

    updateSLA();
    const interval = setInterval(updateSLA, 60000); // Update every minute
    return () => clearInterval(interval);
  }, [ticket]);

  if (!ticket) return null;

  const sentimentColor = ticket.customer.sentiment < 0 
    ? 'text-priority-red drop-shadow-[0_0_8px_rgba(255,42,85,0.5)]' 
    : ticket.customer.sentiment > 0.5 
      ? 'text-green-400 drop-shadow-[0_0_8px_rgba(74,222,128,0.5)]'
      : 'text-slate-400';

  return (
    <div className="w-80 border-l border-navy-700 bg-navy-800/50 hidden xl:flex flex-col h-[calc(100vh-4rem)] overflow-y-auto">
      {/* Customer Header */}
      <div className="p-6 border-b border-navy-700 flex flex-col items-center text-center">
        <div className="relative mb-4">
          <img src={ticket.customer.avatar} alt="avatar" className="w-20 h-20 rounded-full border-2 border-navy-600" />
          <div className={cn(
            "absolute -bottom-2 -right-2 p-1.5 rounded-full bg-navy-800 border border-navy-700",
            sentimentColor
          )}>
            <SafeIcon icon={FiActivity} />
          </div>
        </div>
        <h3 className="text-lg font-medium text-white">{ticket.customer.name}</h3>
        <p className="text-sm text-slate-400">{ticket.customer.company}</p>
        
        <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-navy-900 rounded-full border border-navy-700">
          <SafeIcon icon={FiCreditCard} className="text-cyan-neon text-xs" />
          <span className="text-xs font-mono text-cyan-50">{ticket.customer.plan} Plan</span>
        </div>
      </div>

      {/* SLA Monitor */}
      <div className="p-6 border-b border-navy-700">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">SLA Status</h4>
        <div className={cn(
          "p-4 rounded-xl border flex items-center justify-between",
          isSlaBreaching 
            ? "bg-priority-red/10 border-priority-red/30 glow-red" 
            : "bg-navy-900 border-navy-700"
        )}>
          <span className={cn("text-sm font-medium", isSlaBreaching ? "text-priority-red" : "text-white")}>
            Time to Resolve
          </span>
          <span className={cn("font-mono text-lg font-bold", isSlaBreaching ? "text-priority-red animate-pulse" : "text-cyan-neon")}>
            {timeLeft}
          </span>
        </div>
      </div>

      {/* Macros Quick Insert */}
      <div className="p-6">
        <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
          <SafeIcon icon={FiZap} /> Quick Macros
        </h4>
        <div className="space-y-2">
          {macros.map(macro => (
            <button key={macro.id} className="w-full text-left p-3 rounded-lg border border-navy-700 bg-navy-900 hover:border-cyan-neon/50 hover:bg-navy-700 transition-all group">
              <span className="text-sm text-slate-300 group-hover:text-white block">{macro.title}</span>
              <span className="text-xs text-slate-500 truncate block mt-1">{macro.content}</span>
            </button>
          ))}
        </div>
      </div>
      
      <div className="mt-auto p-4">
         <button className="w-full py-2 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors">
            View full CRM profile <SafeIcon icon={FiExternalLink} />
         </button>
      </div>
    </div>
  );
}